import { colors } from '@lmc/common/constants'
import type { ConsentFormTemplate } from '@lmc/common/types'
import type { ContingencyPlanDTO, Resident } from '@lmc/ts-common'
import { ListSectionHeader, Text } from '@lmc/ui-kit'
import { CarePlanSelector } from 'components/generic'
import type { CarePlanAdlExtended } from 'components/generic/CarePlanSelector/CarePlanSelector'
import { ContingencyPlanSelector } from 'components/generic/ContingencyPlanSelector/ContingencyPlanSelector'
import useFeatureFlags from 'hooks/useFeatureFlags'
import { find } from 'lodash'
import type * as React from 'react'
import {
  type Dispatch,
  type ReactElement,
  type SetStateAction,
  useState
} from 'react'
import { isGpConnectConsentTemplate } from 'screens/Residents/screens/ResidentProfileScreen/screens/GPConnectScreen/constants'
import styled from 'styled-components'
import { getResidentShortCut } from '../utils'
import TextEditor from './TextEditor'

type ModalConsentFormsProps = {
  copyOfTemplates: ConsentFormTemplate[]
  customForms: ConsentFormTemplate[]
  resident: Resident
  setCopyOfTemplates: Dispatch<SetStateAction<any[]>>
  setCustomForms: Dispatch<SetStateAction<any[]>>
  sideControlIds: {
    isSelected: boolean
    title: string
    id: string
  }[]
  templates: any[]
  translate: (key: string, variable?: any) => string
  onCarePlanChange?: (state: Record<string, Array<CarePlanAdlExtended>>) => void
  onContingencyPlanChange?: (
    state: Record<string, Array<ContingencyPlanDTO>>
  ) => void
}

export const ModalConsentForms = ({
  copyOfTemplates,
  resident,
  setCopyOfTemplates,
  sideControlIds,
  templates,
  translate,
  customForms,
  setCustomForms,
  onCarePlanChange,
  onContingencyPlanChange
}: ModalConsentFormsProps): ReactElement => {
  const featureFlags = useFeatureFlags({
    flagNames: ['informed_consent', 'consent_future_plan']
  }).data
  const isInformedConsentEnabled =
    featureFlags['informed_consent']?.enabled || false
  const isConsentFuturePlanEnabled =
    featureFlags['consent_future_plan']?.enabled || false

  const [selectedCarePlans, setSelectedCarePlans] =
    useState<Record<string, Array<CarePlanAdlExtended>>>()
  const [selectedContingencyPlans, setSelectedContingencyPlans] =
    useState<Record<string, Array<ContingencyPlanDTO>>>()
  const handleSelectedCarePlans = (
    carePlans: Array<CarePlanAdlExtended>,
    recordId: string
  ) => {
    setSelectedCarePlans((prevState) => {
      const newState = {
        ...prevState,
        [recordId]: carePlans
      }
      if (onCarePlanChange) {
        onCarePlanChange(newState)
      }
      return newState
    })
  }

  const handleSelectedContingencyPlans = (
    plans: Array<ContingencyPlanDTO>,
    recordId: string
  ) => {
    setSelectedContingencyPlans((prevState) => {
      const newState = {
        ...prevState,
        [recordId]: plans
      }
      if (onContingencyPlanChange) {
        onContingencyPlanChange(newState)
      }
      return newState
    })
  }
  return (
    <>
      <ListSectionHeader
        title={`${resident?.preferredName || resident?.name?.first}${translate(
          'consentsTo'
        )}`}
        shortCut={getResidentShortCut(resident)}
        titleIcon={undefined}
      />
      {customForms?.map((item) => {
        if (!item.isCreated) return null
        return (
          <Wrapper key={item.id}>
            <TextEditor
              isSelected
              item={item}
              resident={resident}
              setCopyOfTemplates={setCustomForms}
              copyOfTemplates={customForms}
              customEntry
            />
          </Wrapper>
        )
      })}

      {templates?.map((item) => {
        const isSelected = find(sideControlIds, {
          isSelected: true,
          id: item.id
        })

        return (
          !!isSelected && (
            <Wrapper key={item.id}>
              <TextEditor
                isSelected={!!isSelected}
                item={item}
                resident={resident}
                setCopyOfTemplates={setCopyOfTemplates}
                copyOfTemplates={copyOfTemplates}
                readOnly={isGpConnectConsentTemplate(item)}
              />
              {isInformedConsentEnabled && (
                <>
                  <CarePlanSelector
                    variant="consent"
                    residentId={resident.id}
                    onChange={(carePlans) =>
                      handleSelectedCarePlans(carePlans, item.id)
                    }
                    selectedCarePlans={selectedCarePlans?.[item.id] || []}
                  />
                  {isConsentFuturePlanEnabled && (
                    <ContingencyPlanSelector
                      residentId={resident.id}
                      onChange={(plans) =>
                        handleSelectedContingencyPlans(plans, item.id)
                      }
                      selectedContingencyPlans={
                        selectedContingencyPlans?.[item.id] || []
                      }
                      label={translate('attachedToFuturePlan')}
                    />
                  )}
                </>
              )}
            </Wrapper>
          )
        )
      })}
      {customForms.length === 1 &&
        !find(sideControlIds, {
          isSelected: true
        }) && (
          <Text style={styles.nothing as React.CSSProperties} as="h4">
            {translate('populateHere')}
          </Text>
        )}
    </>
  )
}

const Wrapper = styled.div`
  border-radius: 4px;
  padding: 9px;
  margin: 16px 0;
  background: ${colors.white};
`

const styles = {
  nothing: {
    textAlign: 'center',
    marginTop: 50,
    color: colors.grey
  }
}

export default ModalConsentForms
